import {
  WSActions,
  WSElement,
  WSFlexBox,
  WSImage,
  WSInfoBox,
  WSList,
  WSPanel,
  WSScreen,
  WSText
} from "@wingspanhq/fe-component-library";
import { OnboardingStep } from "../../../../Onboarding/types";
import React from "react";

import assurelyPreview1 from "../../../assets/AssurelyPreview1.png";
import { FilingAClaim } from "../../../components/FilingAClaim";
import { OAI_KEY, PLAN_DETAILS } from "../../../utils/constants";
import { getPercent } from "../../../../../shared/utils/getPercent";

const { monthlyPremiumFormatted, name, monthlyPremium } = PLAN_DETAILS[OAI_KEY];

const EXAMPLE_AMOUNT_A = 100;
const EXAMPLE_AMOUNT_B = 200;

export const StepHowItWorks: React.FC<OnboardingStep> = props => {
  return (
    <WSPanel>
      <WSList gap="2XL">
        <WSElement>
          <WSText.Paragraph weight="medium" mb="XL">
            How the insurance works
          </WSText.Paragraph>
          <WSText.ParagraphSm color="gray500" mb="L">
            Enrollment is easy, and your coverage is tied directly to your
            earnings on the Wingspan platform using a pay-as-you-go model.
          </WSText.ParagraphSm>
          <WSList gap="S">
            <WSInfoBox title="Cost & deductions" icon="out" theme="neutral">
              <WSText.ParagraphSm color="gray600" mb="XS">
                <b>Premium cost:</b> {monthlyPremiumFormatted} of your
                non-reimbursable gross earnings.
              </WSText.ParagraphSm>
              <WSText.ParagraphSm color="gray600" mb="XS">
                <b>Automatic deductions:</b> The premium is automatically
                deducted from each payment you receive through Wingspan. If you
                don't get paid, you aren't charged.
              </WSText.ParagraphSm>
              <WSText.ParagraphSm color="gray600" mb="XS">
                <b>Transparent pricing:</b> The premium deduction is clearly
                itemized on every payment statement. (Reimbursable expenses are
                excluded from the calculation).
              </WSText.ParagraphSm>
              <WSList.Horizontal wrap="nowrap" equalItems>
                <WSFlexBox.CenterY>
                  <WSElement>
                    <WSText.Paragraph weight="medium" mb="S">
                      Example
                    </WSText.Paragraph>
                    <WSText.ParagraphSm color="gray600" mb="XS">
                      If you are receiving payments from 2 clients (Acme Co. and
                      First Person Inc.):
                    </WSText.ParagraphSm>
                    <WSList>
                      <WSElement>
                        <WSText.ParagraphSm weight="medium">
                          Acme Co. Payment
                        </WSText.ParagraphSm>
                        <ul>
                          <li>Gross earnings = ${EXAMPLE_AMOUNT_A}</li>
                          <li>
                            {monthlyPremiumFormatted} = $
                            {getPercent(monthlyPremium, EXAMPLE_AMOUNT_A)}
                          </li>
                        </ul>
                      </WSElement>
                      <WSElement>
                        <WSText.ParagraphSm weight="medium">
                          First Person Inc. Payment
                        </WSText.ParagraphSm>
                        <ul>
                          <li>Gross earnings = ${EXAMPLE_AMOUNT_B}</li>
                          <li>
                            {monthlyPremiumFormatted} = $
                            {getPercent(monthlyPremium, EXAMPLE_AMOUNT_B)}
                          </li>
                        </ul>
                      </WSElement>
                    </WSList>
                  </WSElement>
                </WSFlexBox.CenterY>
                <WSScreen.TabletAndDesktop>
                  <WSImage
                    src={assurelyPreview1}
                    placeholder={<WSElement py="6XL" shimmer />}
                  />
                </WSScreen.TabletAndDesktop>
              </WSList.Horizontal>
              <WSScreen.Mobile>
                <WSImage
                  src={assurelyPreview1}
                  placeholder={<WSElement py="6XL" shimmer />}
                />
              </WSScreen.Mobile>
            </WSInfoBox>
            <WSInfoBox title="Enrollment & membership" icon="user">
              <b>ODICA membership:</b> Enrollment in this OAI policy
              automatically makes you a member of the On-Demand Independent
              Contractors Association (ODICA), which is required for coverage.
            </WSInfoBox>
            <WSInfoBox title="Managing your policy" icon="bulb">
              <b>Instant compliance:</b> Once enrolled, you can instantly use
              this policy to satisfy OAI or work injury requirements for your
              clients on Wingspan.
              <br />
              <br />
              <b>Cancellation (30 day notice):</b> You can cancel your
              enrollment at any time. By regulation, cancellation requires a
              30-day notice period. Coverage and premium deductions will
              continue for all payments processed during these 30 days.
            </WSInfoBox>
            <FilingAClaim planType={OAI_KEY} />
          </WSList>
        </WSElement>

        <WSActions
          alignment="fill"
          buttons={[
            {
              kind: "Tertiary",
              onClick: props.onBack,
              label: "Back"
            },
            {
              label: "Continue",
              type: "submit",
              onClick: props.onNext
            }
          ]}
        />
      </WSList>
    </WSPanel>
  );
};
